import { useSelector } from 'react-redux';
import { useInvoiceSubmittingStatus } from './useInvoiceSubmittingStatus';
import { useVendorOptions } from './useVendorFields';
import { InvoiceApprovalStatus } from '../../../../../../utils/globalEnums';

function useInvoiceBaseLockReasons() {
  const {
    data: { invoiceItemData },
  } = useSelector((state) => state.invoicing);
  const isSubmitting = useInvoiceSubmittingStatus();
  const invoice = invoiceItemData?.invoice;

  const reasons = [];
  if (isSubmitting) reasons.push('This invoice is currently being submitted.');

  return { reasons, isSubmitting, invoice };
}

// Shared required-fields check — needs glRows since it's component-local state, not Redux
export function useRequiredFieldsFilled() {
  const {
    data: { invoiceItemData, invoiceData },
  } = useSelector((state) => state.invoicing);
  const invoice = invoiceItemData?.invoice;
  const { vendors: autoVendors, manualVendors, facilities: autoFacilities, manualFacilities } = invoiceData;

  const facilities = invoice?.sourceMethod === 'Manual' ? manualFacilities : autoFacilities;
  const bookingTypeId = facilities?.find((f) => f.value === invoice?.facilityId)?.bookingTypeId;

  const vendorOptions = useVendorOptions({
    manualInvoice: invoice?.sourceMethod === 'Manual',
    autoVendors,
    manualVendors,
    bookingTypeId,
  });
  return Boolean(
    invoice?.facilityId > 0 &&
      invoice?.vendorId > 0 &&
      invoice?.vendorInvoiceId !== null &&
      invoice?.glPostingDate !== null &&
      vendorOptions?.some((option) => option.value === invoice.vendorId)
  );
}

// Standardized "missing GLs on invoice items" check (matches SubmitInvoice's original definition).
export function useMissingGlsInInvoiceItems(glRows) {
  const {
    data: { invoiceItemData, invoiceGLItems, invoiceData },
  } = useSelector((state) => state.invoicing);
  const { subcategories: subcats, manualSubcategories } = invoiceData;
  const invoice = invoiceItemData?.invoice;
  const subcategories = invoice?.sourceMethod === null ? subcats : manualSubcategories || [];

  return !(
    glRows?.length > 0 &&
    (invoiceGLItems?.length === 0
      ? !invoiceItemData?.invoiceItems?.filter(
          (item) =>
            !subcategories?.filter(
              (x) =>
                x.facilityId === invoice?.facilityId &&
                x.glCode !== null &&
                x.glCode !== undefined &&
                x.subcategoryId === item?.subcategoryId
            ).length > 0
        )?.length > 0
      : glRows?.every(
          (x) =>
            x.facilityId !== 0 &&
            x.glCode !== null &&
            x.glCode !== undefined &&
            x.glCode !== '' &&
            x.agoraSubcategoryId !== 0
        ))
  );
}

// General editing - blocked by isSubmitting + sent + editingLocked
export function useIsInvoiceEditable(ignoreSentStatus = false) {
  const { reasons, invoice } = useInvoiceBaseLockReasons();
  const allReasons = [...reasons];
  if (invoice?.sent && !ignoreSentStatus) allReasons.push('This invoice has already been sent.');
  if (invoice?.editingLocked) allReasons.push('Missing permission to edit this invoice.');

  return { isEditable: allReasons.length === 0, message: allReasons[0] ?? null };
}

function useInvoiceFieldReasons(glRows) {
  const { reasons, isSubmitting, invoice } = useInvoiceBaseLockReasons();
  const requiredFieldsFilled = useRequiredFieldsFilled();
  const missingGlsInInvoiceItems = useMissingGlsInInvoiceItems(glRows);

  const allReasons = [...reasons];
  if (!requiredFieldsFilled) allReasons.push('Complete invoice info - Required fields are missing');
  if (glRows && missingGlsInInvoiceItems) allReasons.push('Missing GLs on Invoice Details tab');

  return { reasons: allReasons, invoice, isSubmitting };
}

// Submitting - blocked by isSubmitting + sent + submittingLocked + unsaved changes + required fields + missing GLs
export function useCanSubmitInvoice(glRows, unsavedData) {
  const { reasons, isSubmitting, invoice } = useInvoiceFieldReasons(glRows);

  const allReasons = [...reasons];

  const hasUnsavedChanges = Object.values(unsavedData ?? {}).length > 0;
  if (invoice?.sent) allReasons.push('This invoice has already been sent');
  if (invoice?.submittingLocked) allReasons.push('Missing permission to submit this invoice');
  if (hasUnsavedChanges) allReasons.push('Unsaved Changes');
  if (invoice?.approvalStatus === InvoiceApprovalStatus.Pending) allReasons.push('Waiting for approvals');
  return { canSubmit: allReasons.length === 0, message: allReasons[0] ?? null, isSubmitting };
}

// Approvers - blocked by isSubmitting + sent + updateApproversLocked + in queue + required fields + missing GLs
export function useCanUpdateApprovers(glRows) {
  const { reasons, invoice } = useInvoiceFieldReasons(glRows);
  const allReasons = [...reasons];
  if (invoice?.sent) allReasons.push("Approvers can't be changed once the invoice has been submitted.");
  if (invoice?.updateApproversLocked) allReasons.push('Missing permission to update approvers.');
  if (invoice?.queInfo !== null) allReasons.push('Approvers locked — invoice in queue.');
  return { canUpdateApprovers: allReasons.length === 0, message: allReasons[0] ?? null };
}
